import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { ArrowRight } from "@phosphor-icons/react";
import { useSEO } from "@/lib/useSEO";
import { usePrivateMode } from "@/lib/privateMode";
import { LEGAL_DOCS, LEGAL_LINKS, LEGAL_ENTITY, PRODUCT, EFFECTIVE_DATE } from "./legalContent";

export default function LegalPage({ slug: slugProp }) {
  const params = useParams();
  const priv = usePrivateMode();
  const slug = slugProp || params.slug;
  const doc = LEGAL_DOCS[slug];
  useSEO({
    title: doc ? `${doc.title} \u2014 ${PRODUCT}` : PRODUCT,
    description: doc ? doc.intro.replace(/\s+/g, " ").slice(0, 155) : undefined,
    path: doc ? `/${doc.slug}` : "/",
  });
  // Always open a legal page at the very top (client-side nav from the footer keeps the
  // previous scroll position otherwise, so the page appeared to start partway down).
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!doc) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Persistent site navbar (matches the landing page) */}
      <header className="fixed inset-x-0 top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-white/10">
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="legal-home-link">
            <img src="/controlly-icon.png" alt={PRODUCT} className="w-8 h-8 rounded-md" />
            <span className="font-heading text-lg font-extrabold tracking-tight">{PRODUCT}</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            {!priv && <>
            <Link to="/#compare" data-testid="legal-nav-why" className="hidden sm:block text-sm text-muted-foreground hover:text-white transition-colors">Why Controlly</Link>
            <Link to="/#pricing" data-testid="legal-nav-pricing" className="hidden sm:block text-sm text-muted-foreground hover:text-white transition-colors">Pricing</Link>
            <Link to="/login" data-testid="legal-nav-login" className="hidden sm:block text-sm text-muted-foreground hover:text-white transition-colors">Log in</Link>
            <Link
              to="/login"
              data-testid="legal-nav-get-started-cta"
              className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground text-sm font-bold px-4 py-2 hover:bg-primary/90 hover:scale-[1.04] transition-all focus-visible:ring-2 focus-visible:ring-primary/50"
            >
              Start free <ArrowRight size={15} weight="bold" />
            </Link>
            </>}
            {priv && <Link to="/login" data-testid="legal-nav-signin" className="text-sm text-muted-foreground hover:text-white transition-colors">Sign in</Link>}
          </div>
        </nav>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 pt-28 sm:pt-32 pb-12 sm:pb-16" data-testid={`legal-page-${doc.slug}`}>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-primary mb-3">Legal</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">{doc.title}</h1>
        <p className="text-xs text-muted-foreground/70 mt-3 font-mono">Last updated: {EFFECTIVE_DATE} · {LEGAL_ENTITY}</p>

        <p className="mt-8 text-base text-muted-foreground leading-relaxed">{doc.intro}</p>

        <div className="mt-10 space-y-8">
          {doc.sections.map((s) => (
            <section key={s.heading}>
              <h2 className="font-heading text-lg font-bold tracking-tight mb-2">{s.heading}</h2>
              {s.paragraphs?.map((p, i) => (
                <p key={i} className="text-sm text-muted-foreground leading-relaxed mb-2">{p}</p>
              ))}
              {s.bullets && (
                <ul className="mt-1 space-y-1.5">
                  {s.bullets.map((b, i) => (
                    <li key={i} className="flex gap-2 text-sm text-muted-foreground leading-relaxed">
                      <span className="text-primary mt-1.5 w-1 h-1 rounded-full bg-primary shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <div className="mt-14 pt-8 border-t border-white/10">
          <p className="text-xs uppercase tracking-wider text-muted-foreground/60 font-mono mb-3">Other policies</p>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {LEGAL_LINKS.filter((l) => l.to !== `/${doc.slug}`).map((l) => (
              <Link key={l.to} to={l.to} className="text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid={`legal-link-${l.to.slice(1)}`}>
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
